import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { WizardSelection } from '@/components/wizard/types';
import { useFindForm } from '@/hooks/submission_forms/useFindForm';
import WizardCard from './WizardCard';
import WizardNavigation from './WizardNavigation';
import { useCreateSubmission } from '@/hooks/submissions/useCreateSubmission';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { subCategoryDisplayLabel } from './subCategoryLabels';

interface ReviewStepProps {
  selection: WizardSelection;
  owner?: {
    id: number;
    title: string;
    display_id?: string;
  };
  hiddenSteps?: string[];
  onBack?: () => void;
  onReset?: () => void;
}

const ReviewStep: React.FC<ReviewStepProps> = ({
  selection,
  owner,
  hiddenSteps = [],
  onBack,
  onReset,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [siteName, setSiteName] = useState('');
  const subCategories = selection.subCategory ?? [];
  const isSiteCreation = selection.category === 'New Site/Investigator';
  const isSiteNameRequired = isSiteCreation && !subCategories.includes('EAP');
  const ownerId = searchParams.get('owner_id');
  const { findForms, isLoading, error } = useFindForm(selection);
  const {
    createSubmission,
    isLoading: isCreatingSubmission,
    error: createSubmissionError,
  } = useCreateSubmission();

  const handleContinue = async () => {
    try {
      // One template id per chosen sub-category; the first is the primary and the rest
      // are merged into the same submission (OV-1180).
      const formIds = await findForms();
      if (!formIds || formIds.length === 0) return;

      const [primaryFormId, ...additionalFormIds] = formIds;

      const submissionId = await createSubmission({
        submissionFormId: primaryFormId,
        additionalFormIds,
        ownerId: ownerId || undefined,
        siteName: siteName || undefined,
        isSiteCreation,
      });
      if (!submissionId) return;

      // Navigate to form view in edit mode after creation
      navigate(`/submissions/${submissionId}/form?mode=edit`);
    } catch (err) {
      console.error('Failed to process submission:', err);
      // Error is already handled in the hooks, just ensure we don't navigate
    }
  };

  // Auto-selected steps are normally hidden here — the user never actually
  // answered them, so they shouldn't appear as one of "their" answers. But if
  // every populated field is hidden (e.g. Category and Sub-Category are both
  // auto-derived and there's no Variant to show instead), an empty "Your
  // Selection" box is worse than showing an answer the user didn't type
  // themselves — fall back to showing everything that's actually present.
  const variantEntries = Object.entries(selection.variant ?? {});
  const populatedFields: Array<'category' | 'subCategory' | 'variant'> = [
    ...(selection.category ? (['category'] as const) : []),
    ...(subCategories.length > 0 ? (['subCategory'] as const) : []),
    ...(variantEntries.length > 0 ? (['variant'] as const) : []),
  ];
  const allPopulatedFieldsHidden =
    populatedFields.length > 0 &&
    populatedFields.every(field => hiddenSteps.includes(field));
  const isFieldVisible = (field: 'category' | 'subCategory' | 'variant') =>
    allPopulatedFieldsHidden || !hiddenSteps.includes(field);

  const footer = (
    <WizardNavigation
      onBack={onBack}
      onReset={onReset}
      onSubmit={handleContinue}
      disabled={
        isLoading || isCreatingSubmission || (isSiteNameRequired && !siteName)
      }
    />
  );

  return (
    <WizardCard footer={footer}>
      <div className="mb-8">
        {owner && (
          <div className="mb-4 flex flex-col">
            <div className="text-lg font-semibold text-gray-900">
              {owner.title}
            </div>
            <div className="text-sm text-gray-500">
              Parent Submission ID: {owner.display_id}
            </div>
          </div>
        )}
        <h2 className="text-lg font-medium text-gray-900 mb-6">Review</h2>
        <div className="bg-gray-50 rounded-lg p-6 border">
          <h3 className="text-md font-medium text-gray-900 mb-4">
            Your Selection
          </h3>
          <div className="space-y-3">
            {selection.category && isFieldVisible('category') && (
              <div className="flex justify-between">
                <span className="text-gray-600">Category:</span>
                <span className="font-medium text-gray-900">
                  {selection.category}
                </span>
              </div>
            )}
            {subCategories.length > 0 && isFieldVisible('subCategory') && (
              <div className="flex justify-between gap-6">
                <span className="text-gray-600">
                  {subCategories.length > 1
                    ? 'Sub-Categories:'
                    : 'Sub-Category:'}
                </span>
                <span className="font-medium text-gray-900 text-right">
                  {subCategories.map(subCategory => (
                    <span key={subCategory} className="block">
                      {subCategoryDisplayLabel(subCategory)}
                    </span>
                  ))}
                </span>
              </div>
            )}
            {/* One variant per sub-category that has them (OV-1180), so each is
                labelled with the sub-category it belongs to when there are several. */}
            {variantEntries.length > 0 &&
              isFieldVisible('variant') &&
              variantEntries.map(([subCategory, variant]) => (
                <div key={subCategory} className="flex justify-between gap-6">
                  <span className="text-gray-600">
                    {variantEntries.length > 1
                      ? `${subCategoryDisplayLabel(subCategory)} variant:`
                      : 'Variant:'}
                  </span>
                  <span className="font-medium text-gray-900 text-right">
                    {variant}
                  </span>
                </div>
              ))}
          </div>
        </div>
        {isSiteNameRequired && (
          <div className="mt-4">
            <Label className="mb-2" htmlFor="siteName">
              Site Name<span className="text-red-500 ml-1">*</span>
            </Label>
            <Input
              id="siteName"
              type="text"
              value={siteName}
              onChange={e => setSiteName(e.target.value)}
            />
          </div>
        )}

        {error && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm mb-2">
              Error finding form: {error}
            </p>
            <button
              onClick={handleContinue}
              disabled={isLoading}
              className="text-red-700 hover:text-red-900 text-sm underline disabled:opacity-50"
            >
              Try again
            </button>
          </div>
        )}

        {createSubmissionError && (
          <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 text-sm mb-2">
              Error creating submission: {createSubmissionError}
            </p>
          </div>
        )}

        {isLoading && (
          <div className="mt-4 flex items-center justify-center py-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-sm text-gray-600">Finding form...</span>
          </div>
        )}
      </div>
    </WizardCard>
  );
};

export default ReviewStep;
